import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  API,
  cn,
  DEPARTMENT_COLORS,
  STATUS_COLORS,
  DEPARTMENT_LABELS,
  STATUS_LABELS,
  formatDate,
  formatDateTime,
  getDaysUntil,
  isOverdue,
} from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Package,
  MessageSquare,
  Send,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Loader2,
  PlayCircle,
  History,
  ChevronRight,
} from "lucide-react";

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [newComment, setNewComment] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  const [delayReason, setDelayReason] = useState("");
  const [delayDays, setDelayDays] = useState(1);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnToUser, setReturnToUser] = useState("");
  const [targetDateDialogOpen, setTargetDateDialogOpen] = useState(false);
  const [newTargetDate, setNewTargetDate] = useState("");
  const [targetDateReason, setTargetDateReason] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [taskRes, usersRes] = await Promise.all([
          axios.get(`${API}/tasks/${taskId}`, { withCredentials: true }),
          axios.get(`${API}/users`, { withCredentials: true }),
        ]);
        setTask(taskRes.data);
        setUsers(usersRes.data);
      } catch (err) {
        console.error("Failed to fetch task:", err);
        toast.error("Failed to load task");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [taskId]);

  const updateTaskStatus = async (status, additionalData = {}) => {
    setUpdating(true);
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status, ...additionalData },
        { withCredentials: true }
      );
      setTask((prev) => ({ ...prev, status, ...additionalData }));
      toast.success(`Task marked as ${STATUS_LABELS[status]}`);
    } catch (err) {
      console.error("Failed to update task:", err);
      toast.error("Failed to update task");
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedAssignee) return;
    
    setUpdating(true);
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { assigned_to: selectedAssignee },
        { withCredentials: true }
      );
      
      const assignedUser = users.find((u) => u.user_id === selectedAssignee);
      setTask((prev) => ({
        ...prev,
        assigned_to: selectedAssignee,
        assigned_to_name: assignedUser?.name,
      }));
      toast.success("Task assigned successfully");
      setAssignDialogOpen(false);
    } catch (err) {
      console.error("Failed to assign task:", err);
      toast.error("Failed to assign task");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(
        `${API}/tasks/${taskId}/comment`,
        { comment: newComment },
        { withCredentials: true }
      );
      
      setTask((prev) => ({
        ...prev,
        comments: [...(prev.comments || []), response.data.comment],
      }));
      setNewComment("");
      toast.success("Comment added");
    } catch (err) {
      console.error("Failed to add comment:", err);
      toast.error("Failed to add comment");
    }
  };

  const handleMarkDelayed = async () => {
    if (!delayReason.trim()) {
      toast.error("Please provide a reason for delay");
      return;
    }
    if (delayDays < 1) {
      toast.error("Delay must be at least 1 day");
      return;
    }
    await updateTaskStatus("delayed", { delay_reason: delayReason, delay_days: delayDays });
    setDelayDialogOpen(false);
    setDelayReason("");
    setDelayDays(1);
    toast.info(`This task and ${delayDays > 0 ? 'subsequent tasks' : ''} have been delayed by ${delayDays} days`);
  };

  const handleReturnTask = async () => {
    if (!returnReason.trim()) {
      toast.error("Please provide a reason for returning");
      return;
    }
    if (!returnToUser) {
      toast.error("Please select a user to return the task to");
      return;
    }
    await updateTaskStatus("returned", { return_reason: returnReason, return_to: returnToUser });
    setReturnDialogOpen(false);
    setReturnReason("");
    setReturnToUser("");
  };

  const canChangeTargetDate = user && ["super_admin", "admin", "merchandizing_hod"].includes(user.role);

  const handleChangeTargetDate = async () => {
    if (!newTargetDate) {
      toast.error("Please select a new target date");
      return;
    }
    if (!targetDateReason.trim()) {
      toast.error("Reason for change is mandatory");
      return;
    }
    setUpdating(true);
    try {
      const res = await axios.put(
        `${API}/tasks/${taskId}/target-date`,
        { new_target_date: newTargetDate, reason: targetDateReason },
        { withCredentials: true }
      );
      const data = res.data;
      if (data.requires_approval) {
        toast.success("Target date change request submitted for Super Admin approval");
      } else {
        setTask((prev) => ({ ...prev, target_date: newTargetDate }));
        toast.success("Target date updated");
        const refetch = await axios.get(`${API}/tasks/${taskId}`, { withCredentials: true });
        setTask(refetch.data);
      }
      setTargetDateDialogOpen(false);
      setNewTargetDate("");
      setTargetDateReason("");
    } catch (err) {
      console.error("Failed to change target date:", err);
      toast.error(err.response?.data?.detail || "Failed to change target date");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <TaskDetailSkeleton />;
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Task not found</p>
        <Button variant="link" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  const deptColors = DEPARTMENT_COLORS[task.department] || DEPARTMENT_COLORS.merchandizing;
  const statusColors = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
  const daysUntil = getDaysUntil(task.target_date);
  const overdue = isOverdue(task.target_date) && task.status !== "completed";

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="task-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-sm mt-1"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={cn("font-mono text-xs", deptColors.light)}>
              {DEPARTMENT_LABELS[task.department]}
            </Badge>
            <Badge className={cn("font-mono text-xs", statusColors.light)}>
              {STATUS_LABELS[task.status]}
            </Badge>
            {overdue && (
              <Badge variant="destructive" className="font-mono text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
          <h1 className="font-heading text-2xl md:text-3xl tracking-tight">
            {task.task_name}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            {task.item_image_url ? (
              <div className="w-8 h-8 rounded bg-muted overflow-hidden flex-shrink-0">
                <img
                  src={task.item_image_url.startsWith("/api") ? `${API.replace("/api", "")}${task.item_image_url}` : task.item_image_url}
                  alt={task.item_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : null}
            <p className="text-muted-foreground">
              {task.order_number} • {task.item_name}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Task Details */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">
                    Order
                  </p>
                  <p className="font-mono">{task.order_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">
                    Item
                  </p>
                  <div className="flex items-center gap-2">
                    {task.item_image_url ? (
                      <div className="w-8 h-8 rounded bg-muted overflow-hidden flex-shrink-0">
                        <img
                          src={task.item_image_url.startsWith("/api") ? `${API.replace("/api", "")}${task.item_image_url}` : task.item_image_url}
                          alt={task.item_name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    ) : null}
                    <p>{task.item_name}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">
                    Target Date
                  </p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className={cn(overdue && "text-rose-500 font-medium")}>
                      {formatDate(task.target_date)}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">
                    Days to Deadline
                  </p>
                  <p className={cn(overdue ? "text-rose-500" : "text-foreground")}>
                    {task.status === "completed"
                      ? "Completed"
                      : overdue
                      ? `${Math.abs(daysUntil)} days overdue`
                      : `${daysUntil} days`}
                  </p>
                </div>
              </div>

              {task.description && (
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">
                    Description
                  </p>
                  <p className="text-sm">{task.description}</p>
                </div>
              )}

              {task.delay_reason && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-sm">
                  <p className="text-xs font-mono uppercase tracking-widest text-rose-600 mb-1">
                    Delay Reason
                  </p>
                  <p className="text-sm text-rose-700">{task.delay_reason}</p>
                </div>
              )}

              {task.return_reason && (
                <div className="p-3 bg-violet-50 border border-violet-200 rounded-sm">
                  <p className="text-xs font-mono uppercase tracking-widest text-violet-600 mb-1">
                    Return Reason
                  </p>
                  <p className="text-sm text-violet-700">{task.return_reason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({task.comments?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {task.comments && task.comments.length > 0 ? (
                <div className="space-y-3">
                  {task.comments.map((comment) => (
                    <div
                      key={comment.comment_id}
                      className="flex gap-3 p-3 bg-muted/30 rounded-sm"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                          {comment.user_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{comment.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{comment.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No comments yet
                </p>
              )}

              <Separator />

              <div className="flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                  className="rounded-sm"
                  data-testid="comment-input"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="rounded-sm"
                  data-testid="add-comment-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {task.status === "pending" && (
                <Button
                  className="w-full rounded-sm"
                  onClick={() => updateTaskStatus("in_progress")}
                  disabled={updating}
                  data-testid="start-task-button"
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Start Task
                </Button>
              )}

              {(task.status === "pending" || task.status === "in_progress") && (
                <>
                  <Button
                    className="w-full rounded-sm bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => updateTaskStatus("completed")}
                    disabled={updating}
                    data-testid="complete-task-button"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Mark Complete
                  </Button>

                  <Dialog open={delayDialogOpen} onOpenChange={setDelayDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full rounded-sm border-rose-200 text-rose-600 hover:bg-rose-50"
                        data-testid="delay-task-button"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Mark Delayed
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Mark Task as Delayed</DialogTitle>
                        <DialogDescription>
                          Subsequent tasks will be automatically shifted by the delay period.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Delay Duration (days)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={delayDays}
                            onChange={(e) => setDelayDays(parseInt(e.target.value) || 1)}
                            className="rounded-sm"
                            data-testid="delay-days-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Reason for Delay</Label>
                          <Textarea
                            placeholder="Reason for delay..."
                            value={delayReason}
                            onChange={(e) => setDelayReason(e.target.value)}
                            className="rounded-sm"
                            data-testid="delay-reason-input"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setDelayDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleMarkDelayed}
                          disabled={updating}
                          className="bg-rose-500 hover:bg-rose-600"
                          data-testid="confirm-delay-button"
                        >
                          Confirm Delay
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full rounded-sm border-violet-200 text-violet-600 hover:bg-violet-50"
                        data-testid="return-task-button"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Return Task
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Return Task</DialogTitle>
                        <DialogDescription>
                          Select the user to return this task to and provide a reason.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm mb-1 block">Return To</Label>
                          <Select value={returnToUser} onValueChange={setReturnToUser}>
                            <SelectTrigger className="rounded-sm" data-testid="return-to-user-select">
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              {users.filter(u => u.user_id !== user?.user_id).map((u) => (
                                <SelectItem key={u.user_id} value={u.user_id}>
                                  {u.name} ({u.role?.replace(/_/g, " ")})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm mb-1 block">Reason for Returning</Label>
                          <Textarea
                            placeholder="Reason for returning..."
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            className="rounded-sm"
                            data-testid="return-reason-input"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setReturnDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleReturnTask}
                          disabled={updating}
                          className="bg-violet-500 hover:bg-violet-600"
                          data-testid="confirm-return-button"
                        >
                          Return Task
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {canChangeTargetDate && task.status !== "completed" && (
                <Dialog open={targetDateDialogOpen} onOpenChange={setTargetDateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full rounded-sm border-blue-200 text-blue-600 hover:bg-blue-50"
                      data-testid="change-target-date-button"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Change Target Date
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Change Target Date</DialogTitle>
                      <DialogDescription>
                        Current: {formatDate(task.target_date)}. A reason is mandatory.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>New Target Date *</Label>
                        <Input
                          type="date"
                          value={newTargetDate}
                          onChange={(e) => setNewTargetDate(e.target.value)}
                          className="rounded-sm"
                          data-testid="new-target-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Reason for Change *</Label>
                        <Textarea
                          placeholder="Why is the target date being changed?"
                          value={targetDateReason}
                          onChange={(e) => setTargetDateReason(e.target.value)}
                          className="rounded-sm"
                          data-testid="target-date-reason-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setTargetDateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleChangeTargetDate}
                        disabled={updating || !newTargetDate || !targetDateReason.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="confirm-target-date-button"
                      >
                        {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Update Date
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {task.assigned_to ? (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {task.assigned_to_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{task.assigned_to_name}</p>
                    <p className="text-xs text-muted-foreground">Assigned</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not assigned</p>
              )}

              <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full rounded-sm"
                    data-testid="assign-task-button"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {task.assigned_to ? "Reassign" : "Assign Task"}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign Task</DialogTitle>
                    <DialogDescription>
                      Select a team member to assign this task.
                    </DialogDescription>
                  </DialogHeader>
                  <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                    <SelectTrigger className="rounded-sm" data-testid="assignee-select">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter((u) => u.department === task.department || u.role === "admin" || u.role === "super_admin")
                        .map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>
                            {u.name} ({u.role?.replace("_", " ")})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAssign}
                      disabled={!selectedAssignee || updating}
                      data-testid="confirm-assign-button"
                    >
                      Assign
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Change History */}
          {task.change_history && task.change_history.length > 0 && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <History className="w-5 h-5" /> Changes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {task.change_history.slice().reverse().map((change, idx) => (
                    <div key={idx} className="p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs">{change.changed_by_name || "System"}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {formatDateTime(change.changed_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {change.field?.replace(/_/g, " ")}
                        </Badge>
                        <span className="text-muted-foreground line-through">
                          {change.old_value || "—"}
                        </span>
                        <span className="text-muted-foreground">&rarr;</span>
                        <span className="font-medium text-primary">
                          {change.new_value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timestamps */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="font-mono text-xs">{formatDateTime(task.created_at)}</span>
              </div>
              {task.completed_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="font-mono text-xs">
                    {formatDateTime(task.completed_at)}
                  </span>
                </div>
              )}
              {task.is_on_time !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">On Time</span>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "font-mono text-xs",
                      task.is_on_time
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-rose-50 text-rose-700"
                    )}
                  >
                    {task.is_on_time ? "Yes" : "No"}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const TaskDetailSkeleton = () => (
  <div className="max-w-4xl mx-auto space-y-6">
    <Skeleton className="h-24" />
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 space-y-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
      </div>
    </div>
  </div>
);
